import { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Grid,
    IconButton,
    Typography,
    Box,
    Avatar,
    LinearProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Snackbar,
    Alert
} from '@mui/material';
import {
    Close as CloseIcon,
    LocalShipping as DeliveryIcon,
    Person as PersonIcon
} from '@mui/icons-material';
import { postData, updateData, fetchData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';

const DeliveryOutModal = ({
    open,
    onClose,
    onSave,
    deliveryData = null,
    mode = 'create'
}) => {
    const { companyId, branchId, token, userId } = useAuth();

    const [loading, setLoading] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [showSuccess, setShowSuccess] = useState(false);
    const [formData, setFormData] = useState({
        EstimateId: '',
        EstimateDate: '',
        CustomerId: '',
        CustomerName: '',
        ContactNumber1: '',
        ContactNumber2: '',
        AddressLine1: '',
        AddressLine2: '',
        Location: '',
        PinCode: '',
        EventDays: '',
        RentDays: '',
        EventName: '',
        EventLocation: '',
        EventAddress: '',
        DeliveryOutTime: new Date().toTimeString().slice(0, 5),
        DeliveryInTime: '',
        BillDate: new Date().toISOString().split('T')[0],
        BillNumber: '',
        BillPrefix: '',
        EstimateNo: '',
        EnquiryNo: '',
        DeliveryNo: '',
        FinYear: '',
        ProgramDate: '',
        DeliveryDate: '',
        DeliveryReturnDate: '',
        DeliveryOutVehicleNo: '',
        DeliveryOutVehicleName: '',
        Remarks: '',
        items: []
    });

    const [errors, setErrors] = useState({});

    // Fetch bill number for DeliveryCall
    const fetchBillNumberForDeliveryCall = async () => {
        try {
            const response = await fetchData('DeliveryCallApi', `/MaxBillno/${companyId}/${branchId}`, token);
            console.log('Bill number response:', response);
            const data = response.response_data || response || {};
            const billNumber = data.BillNumber || data.billNumber || 1;
            const billPrefix = data.BillPrefix || data.billPrefix || '';
            const finYear = data.FinYear || data.finYear || '';

            const DeliveryNo = `${billPrefix}/${String(billNumber)}/${finYear}`;
            console.log('Generated DeliveryNo:', DeliveryNo);

            setFormData(prev => ({
                ...prev,
                BillNumber: billNumber,
                BillPrefix: billPrefix,
                DeliveryNo: DeliveryNo,
                FinYear: finYear
            }));
        } catch (error) {
            console.error('Error fetching bill number for estimate:', error);
        }
    };


    useEffect(() => {
        if (open && deliveryData) {
            console.log('Loading delivery/estimate data:', deliveryData);

            // Check if it's estimate data (from Estimate Index) or delivery data
            // Delivery data will have DeliveryCallDetails, Estimate data will have EstimateDetails
            const isEstimateData = deliveryData.EstimateDetails && !deliveryData.DeliveryCallDetails;

            if (isEstimateData) {
                // Loading from Estimate (similar to Enquiry → Estimate flow)
                const estimate = deliveryData;
                const details = deliveryData.EstimateDetails || deliveryData.items || [];

                console.log('Loading ESTIMATE data for delivery creation');

                setFormData(prev => ({
                    ...prev,
                    EstimateId: estimate.Id || '',
                    CustomerId: estimate.CustomerId || '',
                    CustomerName: estimate.CustomerName || '',
                    ContactNumber1: estimate.ContactNumber1 || '',
                    ContactNumber2: estimate.ContactNumber2 || '',
                    AddressLine1: estimate.AddressLine1 || '',
                    AddressLine2: estimate.AddressLine2 || '',
                    Location: estimate.Location || '',
                    PinCode: estimate.PinCode || '',
                    EventDays: estimate.EventDays || '',
                    EventName: estimate.EventName || '',
                    EventLocation: estimate.EventLocation || '',
                    EventAddress: estimate.EventAddress || '',
                    DeliveryOutTime: formData.DeliveryOutTime || '',
                    DeliveryInTime: formData.DeliveryInTime || '',
                    EstimateNo: estimate.EstimateNo || '',
                    EnquiryNo: estimate.EnquiryNo || '',
                    DeliveryNo: estimate.estimate || '',
                    ProgramDate: estimate.ProgramDate ? estimate.ProgramDate.split('T')[0] : '',
                    DeliveryDate: estimate.DeliveryDate ? estimate.DeliveryDate.split('T')[0] : '',
                    DeliveryReturnDate: estimate.DeliveryReturnDate ? estimate.DeliveryReturnDate.split('T')[0] : '',
                    DeliveryOutVehicleNo: '',
                    DeliveryOutVehicleName: '',
                    Remarks: estimate.Remarks || '',
                    items: details.map(item => ({
                        ItemId: item.ItemId || '',
                        ItemName: item.ItemName || '',
                        DeliveryOutQuantity: item.Quantity?.toString() || '0',
                        HSNCode: item.HSNCode || null,
                        Rate: item.Rate || null,
                        GSTRate: item.GSTRate || null
                    }))
                }));

                // Fetch bill number only for create mode (estimate data)
                fetchBillNumberForDeliveryCall();
            } else {
                // Loading existing delivery data
                const delivery = deliveryData;
                const details = deliveryData.DeliveryCallDetails || [];

                console.log('Loading EXISTING DELIVERY data for edit/view');
                console.log('Full delivery object:', delivery);
                console.log('Delivery details:', details);
                console.log('DeliveryOutVehicleNo from data:', delivery.DeliveryOutVehicleNo);
                console.log('DeliveryNo from data:', delivery.DeliveryNo);
                console.log('BillNumber from data:', delivery.BillNumber);
                console.log('FinYear from data:', delivery.FinYear);

                // Extract BillPrefix from DeliveryNo if available (format: PREFIX/NUMBER/YEAR)
                let billPrefix = '';
                let billNumber = delivery.BillNumber || '';
                let finYear = delivery.FinYear || '';

                if (delivery.DeliveryNo) {
                    const parts = delivery.DeliveryNo.split('/');
                    if (parts.length === 3) {
                        billPrefix = parts[0];
                        billNumber = parts[1];
                        finYear = parts[2];
                    }
                }

                setFormData(prev => ({
                    ...prev,
                    Id: delivery.Id || '',
                    EstimateId: delivery.EstimateId || '',
                    CustomerId: delivery.CustomerId || '',
                    CustomerName: delivery.CustomerName || '',
                    ContactNumber1: delivery.ContactNumber1 || '',
                    ContactNumber2: delivery.ContactNumber2 || '',
                    AddressLine1: delivery.AddressLine1 || '',
                    AddressLine2: delivery.AddressLine2 || '',
                    Location: delivery.Location || '',
                    PinCode: delivery.PinCode || '',
                    EventDays: delivery.EventDays || '',
                    EventName: delivery.EventName || '',
                    EventLocation: delivery.EventLocation || '',
                    EventAddress: delivery.EventAddress || '',
                    DeliveryOutTime: delivery.DeliveryOutTime || '',
                    DeliveryInTime: delivery.DeliveryInTime || '',
                    BillDate: delivery.BillDate ? delivery.BillDate.split('T')[0] : new Date().toISOString().split('T')[0],
                    BillNumber: billNumber,
                    BillPrefix: billPrefix,
                    DeliveryNo: delivery.DeliveryNo || '',
                    EstimateNo: delivery.EstimateNo || '',
                    EnquiryNo: delivery.EnquiryNo || '',
                    FinYear: finYear,
                    ProgramDate: delivery.ProgramDate ? delivery.ProgramDate.split('T')[0] : '',
                    DeliveryDate: delivery.DeliveryDate ? delivery.DeliveryDate.split('T')[0] : '',
                    DeliveryReturnDate: delivery.DeliveryReturnDate ? delivery.DeliveryReturnDate.split('T')[0] : '',
                    DeliveryOutVehicleNo: delivery.DeliveryOutVehicleNo || '',
                    DeliveryOutVehicleName: delivery.DeliveryOutVehicleName || '',
                    Remarks: delivery.Remarks || '',
                    items: details.map(item => ({
                        Id: item.Id || '', // Include the detail Id for edit
                        ItemId: item.ItemId || '',
                        ItemName: item.ItemName || '',
                        DeliveryOutQuantity: item.DeliveryOutQuantity?.toString() || '0',
                        DeliveryInQuantity: item.DeliveryInQuantity || 0,
                        GoodQuantity: item.GoodQuantity || 0,
                        DamagedQuantity: item.DamagedQuantity || 0,
                        HSNCode: item.HSNCode || null,
                        Rate: item.Rate || null,
                        GSTRate: item.GSTRate || null
                    }))
                }));

                console.log('FormData set with DeliveryOutVehicleNo:', delivery.DeliveryOutVehicleNo);

                // Do NOT fetch bill number for edit/view mode - use existing bill number
            }
        } else if (open && !deliveryData) {
            // Only fetch bill number if opening modal without data (shouldn't happen now)
            fetchBillNumberForDeliveryCall();
        }
    }, [open, deliveryData]);

    // Auto-calculate RentDays when DeliveryDate or DeliveryReturnDate changes
    useEffect(() => {
        if (formData.DeliveryDate && formData.DeliveryReturnDate) {
            const deliveryDate = new Date(formData.DeliveryDate);
            const returnDate = new Date(formData.DeliveryReturnDate);

            // Calculate difference in days
            const diffTime = Math.abs(returnDate - deliveryDate);
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

            // Update RentDays
            setFormData(prev => ({
                ...prev,
                RentDays: diffDays
            }));
        }
    }, [formData.DeliveryDate, formData.DeliveryReturnDate]);

    const handleInputChange = (field, value, itemIndex = null) => {
        if (itemIndex !== null) {
            setFormData(prev => {
                const newItems = [...prev.items];
                newItems[itemIndex] = {
                    ...newItems[itemIndex],
                    [field]: value
                };
                return {
                    ...prev,
                    items: newItems
                };
            });
        } else {
            setFormData(prev => ({
                ...prev,
                [field]: value
            }));

            if (errors[field]) {
                setErrors(prev => ({
                    ...prev,
                    [field]: ''
                }));
            }
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.DeliveryDate) newErrors.DeliveryDate = 'Delivery date is required';
        if (!formData.DeliveryOutVehicleNo) newErrors.DeliveryOutVehicleNo = 'Vehicle number is required';

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSave = async () => {
        if (!validateForm()) return;

        setLoading(true);
        try {
            const deliveryMainData = {
                EstimateId: formData.EstimateId,
                CustomerId: formData.CustomerId,
                CustomerName: formData.CustomerName,
                ContactNumber1: formData.ContactNumber1,
                ContactNumber2: formData.ContactNumber2,
                AddressLine1: formData.AddressLine1,
                AddressLine2: formData.AddressLine2,
                Location: formData.Location,
                PinCode: formData.PinCode,
                // EventName, EventLocation, EventAddress are NotMapped - don't send them
                EventDays: formData.EventDays || '',
                EventName: formData.EventName || '',
                EventLocation: formData.EventLocation || '',
                EventAddress: formData.EventAddress || '',
                DeliveryOutTime: formData.DeliveryOutTime || '',
                RentDays : formData.RentDays || '',
                //DeliveryInTime: formData.DeliveryInTime || '',
                BillDate: formData.BillDate,
                BillNumber: formData.BillNumber,
                DeliveryNo: formData.DeliveryNo,
                FinYear: formData.FinYear,
                EstimateNo: formData.EstimateNo,
                EnquiryNo: formData.EnquiryNo,
                ProgramDate: formData.ProgramDate || null,
                DeliveryDate: formData.DeliveryDate,
                DeliveryReturnDate: formData.DeliveryReturnDate || null,
                DeliveryOutVehicleNo: formData.DeliveryOutVehicleNo,
                DeliveryOutVehicleName: formData.DeliveryOutVehicleName,
                Remarks: formData.Remarks,
                CompanyId: companyId,
                BranchId: branchId,
                CreatedBy: userId,
                ModifiedBy: userId,
                IsActive: true
            };

            const deliveryDetails = formData.items.map(item => ({
                Id: item.Id || undefined, // Include Id for edit mode
                ItemId: item.ItemId,
                ItemName: item.ItemName,
                EstimateId: formData.EstimateId || null,
                DeliveryOutQuantity: parseFloat(item.DeliveryOutQuantity) || 0,
                DeliveryInQuantity: item.DeliveryInQuantity || 0,
                GoodQuantity: item.GoodQuantity || 0,
                DamagedQuantity: item.DamagedQuantity || 0,
                HSNCode: item.HSNCode || null,
                Rate: item.Rate || null,
                GSTRate: item.GSTRate || null,
                IsActive: true
            }));

            const deliveryCallVM = {
                DeliveryCall: deliveryMainData,
                DeliveryCallDetails: deliveryDetails
            };

            console.log('=== Delivery Data to Send ===');
            console.log('DeliveryCall:', deliveryMainData);
            console.log('DeliveryCallDetails:', deliveryDetails);
            console.log('Full payload:', deliveryCallVM);

            let response;
            if (mode === 'edit' && formData.Id) {
                deliveryCallVM.DeliveryCall.Id = formData.Id;
                response = await updateData('DeliveryCallApi', formData.Id, deliveryCallVM, token);
            } else {
                response = await postData('DeliveryCallApi', deliveryCallVM, token);
            }

            console.log('API Response:', response);

            const isSuccess = response && (
                response.responseCode === '1111' ||
                response.responseCode === 1111 ||
                response.success === true
            );

            if (isSuccess) {
                // Show success message
                setSuccessMessage(mode === 'edit' ? 'Delivery updated successfully!' : 'Delivery created successfully!');
                setShowSuccess(true);
                
                // Close modal and refresh data after a short delay
                setTimeout(() => {
                    onSave();
                    onClose();
                }, 1500);
            }
        } catch (error) {
            console.error('Error saving delivery:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleCloseSuccess = () => {
        setShowSuccess(false);
    };

    if (!open) return null;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 2,
                    maxHeight: '95vh',
                    width: '90vw',
                    maxWidth: '1200px'
                }
            }}
        >
            <DialogTitle sx={{
                background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)',
                color: 'white',
                p: 1.5
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 28, height: 28 }}>
                            <DeliveryIcon sx={{ fontSize: 16 }} />
                        </Avatar>
                        <Box>
                            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '0.9rem' }}>
                                {mode === 'view' ? 'View Delivery Out' : mode === 'edit' ? 'Edit Delivery Out' : 'Create Delivery Out'}
                            </Typography>
                        </Box>
                    </Box>
                    <IconButton onClick={onClose} sx={{ color: 'white', p: 0.4 }}>
                        <CloseIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent sx={{ p: 1 }}>
                {loading && <LinearProgress sx={{ mb: 1 }} />}

                <Grid container spacing={1}>
                    {/* Bill Details Section */}
                    <Grid item xs={12}>
                        <Paper elevation={0} sx={{ p: 1, border: '1px solid #e2e8f0', borderRadius: 2, mb: 1 }}>
                            <Typography variant="h6" sx={{ fontWeight: 600, color: '#2d3748', fontSize: '0.75rem', mb: 1.5 }}>
                                Bill Details
                            </Typography>

                            <Grid container spacing={1}>
                                <Grid item xs={12} md={3}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Bill Number"
                                        value={formData.BillPrefix ? `${formData.BillPrefix}/${formData.BillNumber}/${formData.FinYear}` : formData.BillNumber}
                                        InputProps={{ readOnly: true }}
                                        sx={{
                                            '& .MuiInputBase-root': { height: '28px', fontSize: '0.80rem' },
                                            '& .MuiInputLabel-root': { fontSize: '0.80rem', top: '-6px' }
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={3}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Bill Date"
                                        type="date"
                                        value={formData.BillDate}
                                        onChange={(e) => handleInputChange('BillDate', e.target.value)}
                                        InputProps={{ readOnly: mode === 'view' }}
                                        InputLabelProps={{ shrink: true }}
                                        sx={{
                                            '& .MuiInputBase-root': { height: '28px', fontSize: '0.80rem' },
                                            '& .MuiInputLabel-root': { fontSize: '0.80rem' }
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={3}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Estimate Number"
                                        value={formData.EstimateNo}
                                        InputProps={{ readOnly: true }}
                                        sx={{
                                            '& .MuiInputBase-root': { height: '28px', fontSize: '0.80rem' },
                                            '& .MuiInputLabel-root': { fontSize: '0.80rem', top: '-6px' }
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={3}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Enquiry Number"
                                        value={formData.EnquiryNo}
                                        InputProps={{ readOnly: true }}
                                        sx={{
                                            '& .MuiInputBase-root': { height: '28px', fontSize: '0.80rem' },
                                            '& .MuiInputLabel-root': { fontSize: '0.80rem', top: '-6px' }
                                        }}
                                    />
                                </Grid>
                            </Grid>
                        </Paper>
                    </Grid>

                    {/* Customer Information */}
                    <Grid item xs={12}>
                        <Paper elevation={0} sx={{ p: 1, border: '1px solid #e2e8f0', borderRadius: 2, mb: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                                <Avatar sx={{ bgcolor: '#667eea', mr: 1, width: 20, height: 20 }}>
                                    <PersonIcon sx={{ fontSize: 12 }} />
                                </Avatar>
                                <Typography variant="h6" sx={{ fontWeight: 600, color: '#2d3748', fontSize: '0.75rem' }}>
                                    Customer Information
                                </Typography>
                            </Box>

                            <Grid container spacing={1}>
                                <Grid item xs={12} md={4}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Customer Name"
                                        value={formData.CustomerName}
                                        InputProps={{ readOnly: true }}
                                        sx={{
                                            '& .MuiInputBase-root': { height: '28px', fontSize: '0.80rem' },
                                            '& .MuiInputLabel-root': { fontSize: '0.80rem', top: '-6px' }
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Contact 1"
                                        value={formData.ContactNumber1}
                                        InputProps={{ readOnly: true }}
                                        sx={{
                                            '& .MuiInputBase-root': { height: '28px', fontSize: '0.80rem' },
                                            '& .MuiInputLabel-root': { fontSize: '0.80rem', top: '-6px' }
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Location"
                                        value={formData.Location}
                                        InputProps={{ readOnly: true }}
                                        sx={{
                                            '& .MuiInputBase-root': { height: '28px', fontSize: '0.80rem' },
                                            '& .MuiInputLabel-root': { fontSize: '0.80rem', top: '-6px' }
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Address"
                                        value={formData.AddressLine1}
                                        InputProps={{ readOnly: true }}
                                        sx={{
                                            '& .MuiInputBase-root': { height: '28px', fontSize: '0.80rem' },
                                            '& .MuiInputLabel-root': { fontSize: '0.80rem', top: '-6px' }
                                        }}
                                    />
                                </Grid>
                            </Grid>
                        </Paper>
                    </Grid>

                    {/* Delivery Details */}
                    <Grid item xs={12}>
                        <Paper elevation={0} sx={{ p: 1, border: '1px solid #e2e8f0', borderRadius: 2, mb: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                                <Avatar sx={{ bgcolor: '#48bb78', mr: 1, width: 20, height: 20 }}>
                                    <DeliveryIcon sx={{ fontSize: 12 }} />
                                </Avatar>
                                <Typography variant="h6" sx={{ fontWeight: 600, color: '#2d3748', fontSize: '0.75rem' }}>
                                    Delivery Details
                                </Typography>
                            </Box>

                            <Grid container spacing={2}>
                                <Grid item xs={12} md={3}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Event Name"
                                        value={formData.EventName}
                                        InputProps={{ readOnly: true }}
                                        sx={{
                                            '& .MuiInputBase-root': { height: '28px', fontSize: '0.80rem' },
                                            '& .MuiInputLabel-root': { fontSize: '0.80rem', top: '-6px' }
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={3}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Event Location"
                                        value={formData.EventLocation}
                                        InputProps={{ readOnly: true }}
                                        sx={{
                                            '& .MuiInputBase-root': { height: '28px', fontSize: '0.80rem' },
                                            '& .MuiInputLabel-root': { fontSize: '0.80rem', top: '-6px' }
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Event Address"
                                        value={formData.EventAddress}
                                        InputProps={{ readOnly: true }}
                                        sx={{
                                            '& .MuiInputBase-root': { height: '28px', fontSize: '0.80rem' },
                                            '& .MuiInputLabel-root': { fontSize: '0.80rem', top: '-6px' }
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={1}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Event Days"
                                        value={formData.EventDays}
                                        InputProps={{ readOnly: true }}
                                        InputLabelProps={{ shrink: true }}
                                       sx={{
                                            '& .MuiInputBase-root': { height: '28px', fontSize: '0.80rem' },
                                            '& .MuiInputLabel-root': { fontSize: '0.80rem', top: '-6px' }
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={1}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Rent Days"
                                        value={formData.RentDays || ''}
                                        InputProps={{ readOnly: true }}
                                        InputLabelProps={{ shrink: true }}
                                        sx={{
                                            '& .MuiInputBase-root': { height: '28px', fontSize: '0.80rem', bgcolor: '#fef3c7' },
                                            '& .MuiInputLabel-root': { fontSize: '0.80rem', top: '-6px', color: '#f59e0b' }
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={1.5}>                                    
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Program Date"
                                        type="date"
                                        value={formData.ProgramDate}
                                        InputProps={{ readOnly: true }}
                                        InputLabelProps={{ shrink: true }}
                                        sx={{
                                            '& .MuiInputBase-root': { height: '28px', fontSize: '0.80rem' },
                                            '& .MuiInputLabel-root': { fontSize: '0.80rem', top: '-6px' }
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={1.5}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Delivery Date *"
                                        type="date"
                                        value={formData.DeliveryDate}
                                        //onChange={(e) => handleInputChange('DeliveryDate', e.target.value)}
                                        error={!!errors.DeliveryDate}
                                        helperText={errors.DeliveryDate}
                                        // InputProps={{ readOnly: mode === 'view' }}
                                        // InputLabelProps={{ shrink: true }}
                                        InputProps={{ readOnly: true }}
                                        InputLabelProps={{ shrink: true }}
                                        sx={{
                                            '& .MuiInputBase-root': { height: '28px', fontSize: '0.80rem' },
                                            '& .MuiInputLabel-root': { fontSize: '0.80rem' },
                                            '& .MuiFormHelperText-root': { fontSize: '0.65rem', mt: 0.2 }
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={1.5}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Return Date"
                                        type="date"
                                        value={formData.DeliveryReturnDate}
                                        InputProps={{ readOnly: true }}
                                        InputLabelProps={{ shrink: true }}
                                        sx={{
                                            '& .MuiInputBase-root': { height: '28px', fontSize: '0.80rem' },
                                            '& .MuiInputLabel-root': { fontSize: '0.80rem' }
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={1.5}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Delivery Out Time"
                                        type="time"
                                        value={formData.DeliveryOutTime}
                                        onChange={(e) => handleInputChange('DeliveryOutTime', e.target.value)}
                                        InputProps={{ readOnly: mode === 'view' }}
                                        InputLabelProps={{ shrink: true }}
                                        sx={{
                                            '& .MuiInputBase-root': { height: '28px', fontSize: '0.80rem' },
                                            '& .MuiInputBase-input': { color: '#000' },
                                            '& .MuiInputLabel-root': { fontSize: '0.80rem', color: '#000' }
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Vehicle Number *"
                                        value={formData.DeliveryOutVehicleNo}
                                        onChange={(e) => handleInputChange('DeliveryOutVehicleNo', e.target.value)}
                                        error={!!errors.DeliveryOutVehicleNo}
                                        helperText={errors.DeliveryOutVehicleNo}
                                        InputProps={{ readOnly: mode === 'view' }}
                                        InputLabelProps={{ shrink: true }}
                                        sx={{
                                            '& .MuiInputBase-root': { height: '28px', fontSize: '0.80rem' },
                                            '& .MuiInputLabel-root': { fontSize: '0.80rem' },
                                            '& .MuiFormHelperText-root': { fontSize: '0.80rem', mt: 0.2 }
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Vehicle Name"
                                        value={formData.DeliveryOutVehicleName}
                                        onChange={(e) => handleInputChange('DeliveryOutVehicleName', e.target.value)}
                                        error={!!errors.DeliveryOutVehicleName}
                                        helperText={errors.DeliveryOutVehicleName}
                                        InputProps={{ readOnly: mode === 'view' }}
                                        InputLabelProps={{ shrink: true }}
                                        sx={{
                                            '& .MuiInputBase-root': { height: '28px', fontSize: '0.80rem' },
                                            '& .MuiInputLabel-root': { fontSize: '0.80rem' },
                                            '& .MuiFormHelperText-root': { fontSize: '0.80rem', mt: 0.2 }
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Remarks"
                                        multiline
                                        rows={1}
                                        value={formData.Remarks}
                                        onChange={(e) => handleInputChange('Remarks', e.target.value)}
                                        InputProps={{ readOnly: mode === 'view' }}
                                        sx={{
                                            '& .MuiInputBase-root': { height: '28px', fontSize: '0.80rem' },
                                            '& .MuiInputBase-input': { fontSize: '0.80rem', py: 0.5 },
                                            '& .MuiInputLabel-root': { fontSize: '0.80rem' }
                                        }}
                                    />
                                </Grid>
                            </Grid>
                        </Paper>
                    </Grid>

                    {/* Items Table */}
                    <Grid item xs={12}>
                        <Paper elevation={0} sx={{ p: 1, border: '1px solid #e2e8f0', borderRadius: 2 }}>
                            <Typography variant="h6" sx={{ fontWeight: 600, color: '#2d3748', fontSize: '0.75rem', mb: 1.5 }}>
                                Items
                            </Typography>

                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow sx={{ backgroundColor: '#f9fafb' }}>
                                            <TableCell sx={{ fontWeight: 600, fontSize: '0.65rem', py: 0.4, px: 0.75 }}>Item Name</TableCell>
                                            <TableCell sx={{ fontWeight: 600, fontSize: '0.65rem', py: 0.4, px: 0.75, textAlign: 'center' }}>Delivery Out Qty</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {formData.items.map((item, index) => (
                                            <TableRow key={index}>
                                                <TableCell sx={{ fontSize: '0.65rem', py: 0.4, px: 0.75 }}>{item.ItemName}</TableCell>
                                                <TableCell sx={{ py: 0.4, px: 0.75, textAlign: 'center' }}>
                                                    <TextField
                                                        type="number"
                                                        value={item.DeliveryOutQuantity}
                                                        onChange={(e) => handleInputChange('DeliveryOutQuantity', e.target.value, index)}
                                                        InputProps={{ readOnly: mode === 'view' }}
                                                        size="small"
                                                        sx={{
                                                            width: '70px',
                                                            '& .MuiInputBase-input': { fontSize: '0.65rem', textAlign: 'center', py: 0.4 },
                                                            '& .MuiInputBase-root': { height: '24px' }
                                                        }}
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>
                    </Grid>
                </Grid>
            </DialogContent>

            <DialogActions sx={{ p: 1, gap: 1 }}>
                <Button
                    onClick={onClose}
                    variant="outlined"
                    disabled={loading}
                    sx={{
                        borderRadius: '8px',
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        py: 0.4,
                        px: 1.5
                    }}
                >
                    {mode === 'view' ? 'Close' : 'Cancel'}
                </Button>
                {mode !== 'view' && (
                    <Button
                        onClick={handleSave}
                        variant="contained"
                        disabled={loading}
                        sx={{
                            background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)',
                            borderRadius: '8px',
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: '0.75rem',
                            py: 0.4,
                            px: 1.5
                        }}
                    >
                        {loading ? (mode === 'edit' ? 'Updating...' : 'Saving...') : (mode === 'edit' ? 'Update' : 'Save')}
                    </Button>
                )}
            </DialogActions>

            {/* Success Message Snackbar */}
            <Snackbar
                open={showSuccess}
                autoHideDuration={1500}
                onClose={handleCloseSuccess}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert
                    onClose={handleCloseSuccess}
                    severity="success"
                    sx={{
                        width: '100%',
                        fontSize: '0.9rem',
                        fontWeight: 600,
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                    }}
                >
                    {successMessage}
                </Alert>
            </Snackbar>
        </Dialog>
    );
};

export default DeliveryOutModal;
